import type { NavigationProp, ParamListBase } from "@react-navigation/native";
import { Formik } from "formik";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SafeAreaView, ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Button, Icon, useTheme } from "react-native-elements";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { Routes } from "../../constants";
import { onboardingDataSelector, setMenstrualCycle } from "../../slices/onboardingSlice";

interface MenstrualCycleSpacingScreenProps {
  navigation: NavigationProp<ParamListBase>;
}

const MenstrualCycleSpacingScreen: React.FC<MenstrualCycleSpacingScreenProps> = ({ navigation }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const onboardingData = useSelector(onboardingDataSelector);
  const scrollViewRef = useRef<ScrollView>(null);

  // Generate array of possible cycle lengths from 21 to 45
  const cycleLengthOptions = Array.from({ length: 25 }, (_, i) => i + 21);

  const validationSchema = Yup.object().shape({
    knowsCycleLength: Yup.boolean().required(
      t("onboarding.onboarding_menstrual_cycle_spacing_screen.validation_error")
    ),
    cycleLength: Yup.number()
      .min(21)
      .max(35)
      .when("knowsCycleLength", {
        is: true,
        then: Yup.number().required(t("onboarding.onboarding_menstrual_cycle_spacing_screen.validation_error")),
      }),
  });

  type FormSchema = Yup.InferType<typeof validationSchema>;

  const initialValues = { knowsCycleLength: true, cycleLength: 28 } as FormSchema;

  useEffect(() => {
    // Scroll to default value (28) when component mounts
    if (scrollViewRef.current) {
      // Calculate position to scroll to (28 - 21 = index 7)
      // Each option height is approximately 60 (adjust based on your styling)
      const defaultIndex = 7; // Index for 28 days
      const optionHeight = 60;
      // Scroll to position with 2 items above the default value
      scrollViewRef.current.scrollTo({ y: (defaultIndex - 2) * optionHeight, animated: false });
    }
  }, []);

  const onSubmit = (values: FormSchema): void => {
    // Get the menstrual cycle length based on user input
    const cycleLength = values.knowsCycleLength ? values.cycleLength : 28; // Default to 28 if user doesn't know

    // Update the menstrual cycle data in the Redux store
    dispatch(
      setMenstrualCycle({
        // Preserve existing values from previous screen
        lastMenstrualPeriodStartDate: onboardingData.menstrualCycle.lastMenstrualPeriodStartDate,
        // Add the cycle length from this screen
        menstrualCycleLength: cycleLength,
      })
    );

    // Navigate to the next screen
    navigation.navigate(Routes.MenstrualCycleDetailsScreen);
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ handleSubmit, setFieldValue, values, errors, touched }) => (
        <SafeAreaView style={[styles.container, { backgroundColor: theme.colors?.primary?.[10] }]}>
          <View style={styles.header}>
            <Icon name="arrow-back" size={24} onPress={() => navigation.goBack()} testID="back-button" />
          </View>
          <View style={styles.content}>
            <View style={styles.iconContainer}>
              <Icon
                name="calendar"
                type="font-awesome-5"
                size={40}
                color="#fff"
                containerStyle={styles.iconBackground}
              />
            </View>

            <Text style={styles.title}>{t("onboarding.onboarding_menstrual_cycle_spacing_screen.title")}</Text>

            {/* Cycle length options with scroll view */}
            <View style={styles.scrollContainer}>
              {/* Top fade effect */}
              <View style={[styles.fadeTop, { backgroundColor: theme.colors?.primary?.[10], opacity: 0.9 }]} />

              <ScrollView
                ref={scrollViewRef}
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                snapToInterval={60} // Match the height of each option
                decelerationRate="fast"
                onMomentumScrollEnd={(event) => {
                  // Calculate which option is centered
                  const { y } = event.nativeEvent.contentOffset;
                  const optionHeight = 60;
                  // +2 because we show 2 items above the selected one
                  const selectedIndex = Math.round(y / optionHeight) + 2;
                  const selectedValue = cycleLengthOptions[selectedIndex] || 28;
                  setFieldValue("cycleLength", selectedValue);
                }}
              >
                {/* Add empty views at the beginning for padding */}
                <View style={styles.emptyOption} />
                <View style={styles.emptyOption} />

                {cycleLengthOptions.map((days) => (
                  <TouchableOpacity
                    key={days}
                    style={[styles.cycleOption, values.cycleLength === days && styles.selectedOption]}
                    onPress={() => {
                      setFieldValue("knowsCycleLength", true);
                      setFieldValue("cycleLength", days);

                      // Scroll to center the selected option
                      if (scrollViewRef.current) {
                        const index = cycleLengthOptions.indexOf(days);
                        const optionHeight = 60;
                        scrollViewRef.current.scrollTo({
                          y: (index - 2) * optionHeight,
                          animated: true,
                        });
                      }
                    }}
                    testID={`cycle-option-${days}`}
                  >
                    <Text style={[styles.cycleOptionText, values.cycleLength === days && styles.selectedOptionText]}>
                      {days} Days
                    </Text>
                  </TouchableOpacity>
                ))}

                {/* Add empty views at the end for padding */}
                <View style={styles.emptyOption} />
                <View style={styles.emptyOption} />
              </ScrollView>

              {/* Bottom fade effect */}
              <View style={[styles.fadeBottom, { backgroundColor: theme.colors?.primary?.[10], opacity: 0.9 }]} />

              {/* Selection indicator */}
              <View style={styles.selectionIndicator} />
            </View>

            <View style={styles.buttonContainer}>
              <Button
                title={t("onboarding.onboarding_menstrual_cycle_spacing_screen.next_button")}
                buttonStyle={styles.nextButton}
                onPress={() => handleSubmit()}
                testID="next-button"
              />

              <TouchableOpacity
                style={styles.notSureButton}
                onPress={() => {
                  setFieldValue("knowsCycleLength", false);
                  setFieldValue("cycleLength", 28);
                  handleSubmit();
                }}
                testID="not-sure-button"
              >
                <Text style={styles.notSureText}>NOT SURE</Text>
              </TouchableOpacity>
            </View>
          </View>
        </SafeAreaView>
      )}
    </Formik>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
  },
  content: {
    flex: 1,
    alignItems: "center",
    padding: 16,
  },
  iconContainer: {
    marginTop: 20,
    marginBottom: 16,
  },
  iconBackground: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: "center",
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginTop: 16,
    marginBottom: 40,
    textAlign: "center",
  },
  scrollContainer: {
    height: 300,
    width: "100%",
    alignItems: "center",
    position: "relative",
  },
  scrollView: {
    width: "100%",
    height: 300,
  },
  fadeTop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 120,
    // backgroundColor set dynamically in component
    zIndex: 1,
    pointerEvents: "none",
  },
  fadeBottom: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 120,
    // backgroundColor set dynamically in component
    zIndex: 1,
    pointerEvents: "none",
  },
  selectionIndicator: {
    position: "absolute",
    left: "10%",
    right: "10%",
    height: 60,
    top: 120,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#e0e0e0",
    zIndex: 0,
    pointerEvents: "none",
  },
  cycleOption: {
    width: "100%",
    height: 60,
    justifyContent: "center",
    alignItems: "center",
  },
  emptyOption: {
    width: "100%",
    height: 60,
  },
  selectedOption: {
    // Visual indicator for selected option
  },
  cycleOptionText: {
    fontSize: 18,
    color: "#777",
  },
  selectedOptionText: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#333",
  },
  buttonContainer: {
    width: "100%",
    marginTop: "auto",
    paddingBottom: 20,
  },
  nextButton: {
    paddingVertical: 12,
    paddingHorizontal: 32,
    marginTop: 16,
    borderRadius: 8,
    width: "80%",
    alignSelf: "center",
  },
  notSureButton: {
    alignItems: "center",
    padding: 16,
    marginTop: 12,
  },
  notSureText: {
    fontSize: 16,
    color: "#777",
  },
});

export default MenstrualCycleSpacingScreen;
